import { useLastNonNull } from "@lib/Hooks"
import { serviceItemIdsToDeleteWithWorkOrder } from "@model/workOrders/WorkOrderDeletionUtil"
import { labelForWorkRequest, WorkRequest, WorkRequestType } from "@model/workRequests/WorkRequest"
import { Button, ButtonType } from "@ui/common/buttons/Button"
import { FormField, FormFieldDirection, FormFieldLabel, FormFieldNote } from "@ui/common/form/FormField"
import { TextArea } from "@ui/common/form/TextArea"
import { Grid, GridGap, GridJustification } from "@ui/common/grids/Grid"
import { Modal, ModalBody, ModalButtons, ModalContent, ModalHeader, ModalHeaderTitle } from "@ui/common/Modal"
import React from "react"
import { FormDivider, FormDividerMargin } from "../common/form/FormDivider"
import { Radio } from "../common/form/Radio"

export function DismissWorkOrderModal({
    onReasonChanged,
    onCancel,
    onConfirm,
    onWorkRequestsToDismissChanged,
    itemDescriptionTerminology,
    dismissActionDescription,
    returnToTheQueueActionDescription,
    customServiceRepairMessage,
    customPreventativeAndInspectionServicesMessage,
    deleteAllServiceItems = false,
    ...props
}: {
    data: { reason: string; workRequests: WorkRequest[]; workRequestsToDelete: number[] } | null
    hasDismissWorkRequestPermission: boolean
    /**
     * Action Item #10455: when set (Closed Work Orders) the user is not offered the
     * Delete/Decline choice for the associated Service Items. Every Service Item on the
     * Work Order is deleted along with it.
     */
    deleteAllServiceItems?: boolean
    onReasonChanged: (value: string) => void
    onWorkRequestsToDismissChanged: (workRequestId: number, shouldDismiss: boolean) => void
    itemDescriptionTerminology?: string | null
    dismissActionDescription?: string | null
    returnToTheQueueActionDescription?: string | null
    customServiceRepairMessage?: string | null
    customPreventativeAndInspectionServicesMessage?: string | null
    onCancel: () => void
    onConfirm: (reason: string, workRequestsToDelete: number[]) => void
}): JSX.Element {
    const data = useLastNonNull(props.data)
    const isVisible = props.data !== null
    const itemTerm = itemDescriptionTerminology ?? "Service Request"
    const itemTermPlural = itemDescriptionTerminology ? `${itemDescriptionTerminology}s` : "Service Requests"

    const dismissActionText = dismissActionDescription ?? "Dismiss"
    const returnToTheQueueActionText = returnToTheQueueActionDescription ?? "Return"

    if (data === null) return <Modal isVisible={isVisible} />

    return (
        <Modal isVisible={isVisible} useContentTag={true} onClose={onCancel}>
            <ModalContent>
                <ModalHeader>
                    <ModalHeaderTitle>Delete Work Order</ModalHeaderTitle>
                </ModalHeader>
                <ModalBody>
                    <Grid columns={1}>
                        {deleteAllServiceItems ? (
                            <div>
                                Are you sure you want to delete this Work Order?{" "}
                                <strong>This action cannot be undone.</strong> Deleting a Work Order will not
                                undo any associated meter sync. If there are any Service Items being deleted
                                that are associated to a Service Schedule, the schedule itself will not be
                                affected. All attachments on associated Service Items will be permanently
                                lost.
                            </div>
                        ) : (
                            <>
                            <div>
                                Are you sure you wish to delete this Work Order?{" "}
                                <strong>This action cannot be undone.</strong>
                            </div>
                            {data.workRequests.filter(x => x.workRequestType == WorkRequestType.Inspection || x.workRequestType == WorkRequestType.Preventative).length > 0 && (
                                <>
                                    <FormDivider margin={FormDividerMargin.None} />
                                    <FormField label={"Preventative Maintenance and Inspections"} direction={FormFieldDirection.Column} className={"radio-list"}>
                                        <FormFieldNote>
                                            {customPreventativeAndInspectionServicesMessage ? customPreventativeAndInspectionServicesMessage :
                                                <>
                                                    The following scheduled {itemTermPlural.toLowerCase()} will be returned to the {itemTerm} queue.
                                                </>
                                            }
                                        </FormFieldNote>
                                        <Grid columns={2} justifyItems={GridJustification.End} style={{ minHeight: "10px" }}>
                                            <FormFieldLabel></FormFieldLabel>
                                            <FormFieldLabel className={"radio-column-title"}>{returnToTheQueueActionText}</FormFieldLabel>
                                        </Grid>
                                        {data.workRequests.filter(x => x.workRequestType == WorkRequestType.Inspection || x.workRequestType == WorkRequestType.Preventative).map((it) => (
                                            <Grid columns={2} justifyItems={GridJustification.Stretch} key={it.id}>
                                                <FormFieldLabel style={{ fontWeight: "normal", fontStyle: "italic" }}>{labelForWorkRequest(it)}</FormFieldLabel>
                                                <Grid columns={1} justifyItems={GridJustification.End}>
                                                    <Radio
                                                        label={""}
                                                        isChecked={true}
                                                    />
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </FormField>
                                </>
                            )}
                            {data.workRequests.filter(x => x.workRequestType == WorkRequestType.Service).length > 0 && (
                                <>
                                    <FormDivider margin={FormDividerMargin.None} />
                                    <FormField label={itemTermPlural} direction={FormFieldDirection.Column} className={"radio-list"}>
                                        <FormFieldNote>
                                            {customServiceRepairMessage ? customServiceRepairMessage :
                                                <>
                                                    The following {itemTermPlural.toLowerCase()} can be dismissed or returned to the {itemTerm} queue.
                                                </>
                                            }
                                        </FormFieldNote>
                                        <Grid columns={2} justifyItems={GridJustification.Stretch} style={{ minHeight: "10px" }}>
                                            <FormFieldLabel></FormFieldLabel>
                                            <FormField>
                                                <Grid columns={4} gap={GridGap.Small} justifyItems={GridJustification.End}>
                                                    <label></label>
                                                    <label></label>
                                                    {props.hasDismissWorkRequestPermission ? <FormFieldLabel className={"radio-column-title"}>{dismissActionText}</FormFieldLabel> : <label></label>}
                                                    <FormFieldLabel className={"radio-column-title"}>{returnToTheQueueActionText}</FormFieldLabel>
                                                </Grid>
                                            </FormField>
                                        </Grid>
                                        {data.workRequests.filter(x => x.workRequestType == WorkRequestType.Service).map((it) => (
                                            <Grid columns={2} justifyItems={GridJustification.Stretch} key={it.id}>
                                                <FormFieldLabel style={{ fontWeight: "normal", fontStyle: "italic" }}>{labelForWorkRequest(it)}</FormFieldLabel>
                                                <FormField>
                                                    <Grid columns={4} gap={GridGap.Small} justifyItems={GridJustification.End}>
                                                        <label></label>
                                                        <label></label>
                                                        {props.hasDismissWorkRequestPermission ?
                                                            <Radio
                                                                label=""
                                                                isChecked={data.workRequestsToDelete.includes(it.id)}
                                                                onSelected={() => onWorkRequestsToDismissChanged(it.id, true)}
                                                            /> : <label></label>}
                                                        <Radio
                                                            label=""
                                                            isChecked={!data.workRequestsToDelete.includes(it.id)}
                                                            onSelected={() => onWorkRequestsToDismissChanged(it.id, false)}
                                                        />
                                                    </Grid>
                                                </FormField>
                                            </Grid>
                                        ))}
                                    </FormField>
                                </>
                            )}
                            </>
                        )}
                        <FormDivider margin={FormDividerMargin.None} />
                        <FormField label={"Reason*"}>
                            <TextArea onChange={onReasonChanged} value={data.reason} maxLength={250} />
                        </FormField>
                    </Grid>
                </ModalBody>
                <ModalButtons>
                    <Button label="Cancel" type={ButtonType.Text} onClick={onCancel} />
                    <Button
                        label="Delete Work Order"
                        isEnabled={data.reason.trim() !== ""}
                        onClick={() =>
                            onConfirm(
                                data.reason,
                                deleteAllServiceItems
                                    ? serviceItemIdsToDeleteWithWorkOrder(data.workRequests)
                                    : data.workRequestsToDelete
                            )
                        }
                    />
                </ModalButtons>
            </ModalContent>
        </Modal>
    )
}
